import { router } from '@/router/index'
import { useSwalAlert } from '@/composables/useSwalAlert'

export const useUserStore = defineStore('userStore', () => {
  const roles = ref(null)
  const permission = ref(null)
  const auth = ref('Unauthenticated')
  const user = ref(null)
  const loginError = ref(null)
  const level = ref(50)
  const REACTRefresCookie = ref(0)

  if (localStorage.getItem('Authenticated') == 1 && auth.value == 'Unauthenticated') {
    auth.value = 'Authenticated'
  }

  async function userAuthCheck() {
    if (auth.value === 'Authenticated' || localStorage.getItem('Authenticated')) {
      const { data, status, promise } = useFetch('/api/v1/auth/check')
      await promise
      if (data.value.status === 'Success' && status.value === 200) {
        onAuthenticateSuccess(data.value.data)
        REACTRefresCookie.value++
      } else {
        auth.value = 'Unauthenticated'
        localStorage.setItem('Authenticated', 0)
      }
    }
    return new Promise(resolve => {
      resolve()
    })
  }

  async function doLogin(email, password, code = null) {
    const body = { email: email, password: password }
    if (code) {
      body.code = code
    }
    const { data, promise } = useFetch('/api/v1/login', body, 'POST')
    await promise
    if (data.value.status === 'Success') {
      onAuthenticateSuccess(data.value.data)
      router.push('/dashboard')
    } else {
      loginError.value = data.value.message
    }
  }

  function onAuthenticateSuccess(data) {
    localStorage.setItem('Authenticated', 1)
    auth.value = 'Authenticated'
    roles.value = data.roles.map(element => element.toLowerCase())
    permission.value = data.permissions
    user.value = data.user
    level.value = data.level
  }

  async function doLogout() {
    console.log('user.value', user.value)

    const { status, promise } = useFetch('/api/v1/logout', {}, 'POST')
    await promise
    if (unref(status) === 204) {
      auth.value = 'Unauthenticated'
      localStorage.setItem('Authenticated', 0)
      if (userHasRole('company_administrator')) router.push('/companies/login')
      else router.push('/login')
    }
  }

  async function updateAboutMe(password, passwordConfirmation) {
    try {
      const url = `/api/v1/users/${user.value.id}`
      const body = {
        id: user.value.id,
        first_name: user.value.first_name,
        family_name: user.value.family_name,
        phone: user.value.phone,
        email: user.value.email
      }

      if (password) {
        body.password = password
        body.password_confirmation = passwordConfirmation
      }

      const { data, promise } = useFetch(url, body, 'PUT')
      await promise
      if (data.value.status === 'Success') {
        useSwalAlert('Changes saved successfully')
      } else {
        return data.value.message
      }
    } catch (error) {
      console.error(error)
    }
  }

  async function updateCredentials(password, passwordConfirmation) {
    if (!password || (password && password.length < 8)) {
      useSwalAlert('Password has to content at least 8 characters', 'error')
      return
    }
    if (!isPasswordConfirmCorrect(password, passwordConfirmation)) {
      useSwalAlert("Passwords don't match", 'error')
      return
    }
    try {
      const url = `/api/v1/users/${user.value.id}`
      const body = {
        id: user.value.id,
        password: password,
        password_confirmation: passwordConfirmation,
        email: user.value.email
      }
      const { data, promise } = useFetch(url, body, 'PUT')
      await promise
      if (data.value.status === 'Success') {
        useSwalAlert('Changes saved successfully')
      } else {
        return data.value.message
      }
    } catch (error) {
      console.error(error)
    }
  }

  function isPasswordConfirmCorrect(password, passwordConfirmation) {
    if (password !== passwordConfirmation) return false
    return true
  }

  function userHasRole(role) {
    return roles.value.includes(role.toLowerCase())
  }

  function hasPermission(entity, action) {
    if (userHasRole('SuperAdmin')) return true

    let result =
      permission.value.some(p => p.name === `${entity}.${action}`) ||
      permission.value.some(p => p.name === `${entity}.${action}.authorized`)

    if (action == 'store' && !result) {
      result = permission.value.some(p => p.name === `${entity}.create`)
    }
    return result
  }

  function hasRole(role) {
    return roles.value.includes(role)
  }

  function isAuthenticated() {
    return auth.value === 'Autenticated'
  }

  function getId() {
    return user.value?.id ?? null
  }

  function getCustomerId() {
    return user.value?.customer_id ?? null
  }

  function getInitials() {
    let userName = 'User'
    let userLastname = 'Name'
    if (user.value.first_name) {
      userName = user.value.first_name
    }
    if (user.value.family_name) {
      userLastname = user.value.family_name
    }
    const firstLetter = userName.split('')[0]
    const secondLetter = userLastname.split('')[0]
    return firstLetter.toUpperCase() + secondLetter.toUpperCase()
  }

  function getCustomerName() {
    return user.value.customer.name
  }

  function getUserLevel() {
    return level.value
  }

  function doIHaveEnoughLevel(levelInput) {
    return levelInput >= level.value
  }

  function getUser() {
    return user.value
  }

  return {
    getInitials,
    getCustomerName,
    getUserLevel,
    doIHaveEnoughLevel,
    updateAboutMe,
    updateCredentials,
    isPasswordConfirmCorrect,
    roles,
    permission,
    hasPermission,
    auth,
    user,
    REACTRefresCookie,
    userAuthCheck,
    doLogin,
    doLogout,
    loginError,
    userHasRole,
    hasRole,
    isAuthenticated,
    getId,
    getCustomerId,
    getUser
  }
})
